import Phaser from 'phaser';
import { SIM, COLORS, DESIGN } from '@/config';
import { stairwellSegments } from '@/battle/geometry';
import { World, type GameData, type SimEvent } from '@/battle/World';
import { Enemy } from '@/battle/entities/Enemy';
import { Unit } from '@/battle/entities/Unit';
import { type Level, type SlotType } from '@/data/schema/level';
import { UNITS, ENEMIES } from '@/data/registry';
import { getLevel } from '@/data/levelRegistry';
import { displayLevel } from '@/battle/Upgrades';
import { sfx, type SfxName } from '@/audio/Sfx';
import { CameraController } from '@/util/CameraController';
import type { TargetMode } from '@/data/schema/common';

/** The stage the battle defaults to if launched without one (e.g. dev/reload). */
const DEFAULT_LEVEL_ID = 'stage_01_olympus';

const ENEMY_TINT: Record<string, number> = {
  shade: 0xb9bfe0,
  bronze_brute: 0xc98b3a,
  fury_swarmling: 0xe0556c,
  harpy: 0x8ad0e0,
  bronze_myrmidon: 0xd6a24c, // lighter bronze than the brute
  bronze_shard: 0xe0bd6a, // bright bronze sliver
  talos_sentinel: 0xb87a2e, // deep weathered bronze — the giant
  molten_myrmidon: 0xe07a3a, // forge-hot bronze — glows orange, can't be chilled
  gilded_eagle: 0xf0d070, // gleaming gold flyer
  forge_warden: 0xd06028, // molten mini-boss — angrier orange than the myrmidon
};
const FAMILY_COLOR: Record<string, number> = {
  melee: 0xc05a5a,
  rangedPhysical: 0x5ac0a0,
  magic: 0x7e8af0,
  support: 0xc0a85a,
};
/** On-screen diameter (px) per enemy token, scaled by ROLE so size telegraphs
 *  threat: swarm < skirmisher/flyer < line < tank < mini-boss < boss. */
const ENEMY_SIZE: Record<string, number> = {
  fury_swarmling: 24, // chaff — smallest, comes in packs
  harpy: 34, // nimble flyer
  shade: 40, // baseline footsoldier
  bronze_brute: 66, // armored tank — clearly bulkier
  unmade_ravager: 82, // mini-boss
  unmade_colossus: 108, // boss — towers over the roster
  bronze_shard: 26, // shattered chaff — fast and small
  bronze_myrmidon: 50, // armored line infantry, between shade and brute
  talos_sentinel: 118, // THE bronze giant — largest token in the game
  molten_myrmidon: 52, // forge-hot line infantry — the slowImmune backbone
  gilded_eagle: 40, // armored flyer — bigger/tougher than a harpy
  forge_warden: 88, // slowImmune mini-boss that shatters into shards
};
const ENEMY_SIZE_DEFAULT = 40;
/** A deployed unit is a side-view BUILDING. `h` is its on-screen height (px); its
 *  base is anchored on the platform top, which sits BUILD_BASE_OFFSET below the
 *  slot centre (stage-1 slots are authored that far above the ledge). A ranged
 *  building's `crew` mans its firing deck — one figure per multiShot. */
const BUILD_BASE_OFFSET = 34;
interface CrewSpec {
  y: number;
  spread: number;
  kind: 'archer' | 'mote';
}
interface BuildingSpec {
  h: number;
  crew?: CrewSpec;
}
const BUILDINGS: Record<string, BuildingSpec> = {
  footmen: { h: 50 },
  archers: { h: 76, crew: { y: -28, spread: 22, kind: 'archer' } },
  mage: { h: 82, crew: { y: -40, spread: 14, kind: 'mote' } },
  frost_spire: { h: 80, crew: { y: -44, spread: 12, kind: 'mote' } },
  pilgrim_warden: { h: 62 },
};
const BUILDING_FALLBACK: BuildingSpec = { h: 54 };
/** Spacing (px) between soldier bodies in a melee squad. */
const SQUAD_SPACING = 20;
/** A spawned soldier's token diameter, march speed (px/s), and how far back from
 *  its foe it plants (so it stands beside the enemy, not on top of it). */
const SOLDIER_TOKEN = 22;
const SOLDIER_SPEED = 240;
const SOLDIER_STANDOFF = 12;
/** Projectile sprite flung per attacking family (melee strikes have no shot). */
const PROJECTILE_FX: Record<string, string> = {
  rangedPhysical: 'fx_arrow',
  magic: 'fx_bolt',
  support: 'fx_mote',
};
/** The firing sound per attacking family (melee strikes go through the sim's
 *  `damage` event, so they get the `hit` cue rather than a shot). */
const SHOT_SFX: Record<string, SfxName> = {
  rangedPhysical: 'shot_ranged',
  magic: 'shot_magic',
  support: 'shot_support',
};
/** Display length (px) of each projectile sprite. */
const PROJECTILE_SIZE: Record<string, number> = {
  fx_arrow: 26,
  fx_bolt: 22,
  fx_mote: 18,
};
/** A deployed unit's glyph by family (hero overrides). */
function unitGlyph(family: string, isHero: boolean): string {
  if (isHero) return '♛';
  switch (family) {
    case 'melee':
      return '⚔';
    case 'magic':
      return '❄';
    case 'support':
      return '✦';
    default:
      return '➤';
  }
}

interface EnemyView {
  container: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  hp: Phaser.GameObjects.Rectangle;
  shadow?: Phaser.GameObjects.Ellipse;
  /** Cue shown while the enemy is "Exposed" (held by an expose-blocker → +dmg). */
  expose: Phaser.GameObjects.Text;
  /** Rime overlay shown while chilled (slowed); layered over the blue tint. */
  frost?: Phaser.GameObjects.Image;
  baseTint: number;
  flying: boolean;
}
/** One soldier body in a melee squad's view: a small token that walks out from
 *  the building to the foe it holds, with its own HP bar + respawn timer. Lives
 *  in world space (NOT nested in the building container) so it can roam. */
interface SoldierView {
  container: Phaser.GameObjects.Container;
  token: Phaser.GameObjects.GameObject;
  hp: Phaser.GameObjects.Rectangle;
  respawn: Phaser.GameObjects.Text;
  /** Current world position (lerped toward the hold/engage point each frame). */
  x: number;
  y: number;
  /** False until first positioned — so it marches out from the building once. */
  placed: boolean;
}
interface UnitView {
  container: Phaser.GameObjects.Container;
  pips: Phaser.GameObjects.Text;
  /** Texture key currently drawn — rebuilt when a branch upgrade swaps the art. */
  key: string;
  /** Melee: one view per soldier body (spread along the line). Empty for ranged. */
  soldiers: SoldierView[];
  /** Ranged: the projectile-source icons (one per multiShot). Absent for melee. */
  shots?: Phaser.GameObjects.Container;
  /** multiShot count last drawn — rebuild the shot icons when it changes. */
  shotCount: number;
  /** Building footprint (container-local) so the selection outline hugs it. */
  bw: number;
  btop: number;
  bbot: number;
}
interface Tracer {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  life: number;
  color: number;
}
interface FloatText {
  text: Phaser.GameObjects.Text;
  life: number;
  vy: number;
}

export class BattleScene extends Phaser.Scene {
  level!: Level;
  world!: World;
  /** Launch args, retained so Replay restarts the same stage at the same tier. */
  levelId = DEFAULT_LEVEL_ID;
  difficultyTier: number | undefined;

  private accumulatorMs = 0;
  speed = 1;
  paused = false;
  /** Last observed world phase — drives the wave-start / win / lose audio cues. */
  private lastPhase = '';

  /** Selected slot for the HUD panel; bumped uiVersion signals HUD to rebuild. */
  selectedSlotId: string | null = null;
  /** A unit staged on the selected (empty) slot but not yet built — shown as a
   *  translucent ghost + range ring so the player confirms placement first. */
  draftUnitId: string | null = null;
  /** Two-tap sell guard: true once Sell is tapped, awaiting a confirm/cancel. */
  pendingSell = false;
  uiVersion = 0;

  private slotViews = new Map<string, { x: number; y: number; type: SlotType; ring: Phaser.GameObjects.Graphics; label: Phaser.GameObjects.Text }>();
  private enemyViews = new Map<Enemy, EnemyView>();
  private unitViews = new Map<string, UnitView>();

  private fxLayer!: Phaser.GameObjects.Graphics;
  private overlay!: Phaser.GameObjects.Graphics;
  /** Translucent token shown at the slot while a deploy is being drafted. */
  private draftToken?: Phaser.GameObjects.Image;
  private tracers: Tracer[] = [];
  private floats: FloatText[] = [];
  private floatPool: Phaser.GameObjects.Text[] = [];

  constructor() {
    super('battle');
  }

  create(args?: { levelId?: string; difficultyTier?: number }): void {
    // Remember the launch args so a Replay (scene.restart with no data) re-runs
    // the SAME stage at the SAME difficulty instead of falling back to defaults.
    this.levelId = args?.levelId ?? this.levelId ?? DEFAULT_LEVEL_ID;
    this.difficultyTier = args?.difficultyTier ?? this.difficultyTier;

    const level = getLevel(this.levelId);
    if (!level) throw new Error(`BattleScene: no built level "${this.levelId}"`);
    this.level = level;
    const data: GameData = {
      level: this.level,
      units: UNITS,
      enemies: ENEMIES,
      difficultyTier: this.difficultyTier,
    };
    this.world = new World(data, 1234);

    this.accumulatorMs = 0;
    this.speed = 1;
    this.paused = false;
    this.selectedSlotId = null;
    this.draftUnitId = null;
    this.pendingSell = false;
    this.draftToken = undefined; // a fresh scene; the old object was destroyed
    this.lastPhase = '';

    this.enemyViews.clear();
    this.unitViews.clear();
    this.tracers = [];
    this.floats = [];
    // scene.restart() destroys every GameObject but keeps THIS instance's
    // fields — so the float pool would hand back destroyed Text objects (a
    // `drawImage(null)` crash on the first popup of the replay). Drop them.
    this.floatPool = [];

    this.drawWorld();
    this.overlay = this.add.graphics().setDepth(8);
    this.fxLayer = this.add.graphics().setDepth(14);
    new CameraController(this, {
      worldWidth: this.level.world.width,
      worldHeight: this.level.world.height,
      viewWidth: DESIGN.width,
      onTap: (x, y) => this.handleTap(x, y),
    });

    if (!this.scene.isActive('hud')) this.scene.launch('hud');
  }

  // ---- world geometry ------------------------------------------------------

  private drawWorld(): void {
    const { world, platforms, slots, path } = this.level;

    // Resolve the backdrop per-stage: a stage-specific plate (`world_background_
    // <id>`) wins, then the legacy Olympus-1 plate, then the hand-authored SVG,
    // then a flat gradient — so every stage has a sky even mid-art-pass.
    const stageKey = `world_background_${this.level.id}`;
    const bgKey = this.textures.exists(stageKey)
      ? stageKey
      : this.textures.exists('world_background')
        ? 'world_background'
        : this.textures.exists('world_background_svg')
          ? 'world_background_svg'
          : null;
    if (bgKey) {
      this.add
        .image(0, 0, bgKey)
        .setOrigin(0, 0)
        .setDisplaySize(world.width, world.height)
        .setDepth(0);
    } else {
      const bg = this.add.graphics().setDepth(0);
      bg.fillGradientStyle(COLORS.bgTop, COLORS.bgTop, COLORS.bgBottom, COLORS.bgBottom, 1);
      bg.fillRect(0, 0, world.width, world.height);
    }

    // Platforms are the walkable steps now (enemies stand on the top face); draw
    // them as solid carved-marble ledges with a lit tread along the top surface.
    const pg = this.add.graphics().setDepth(1);
    for (const p of platforms) {
      pg.fillStyle(0x0e1020, 0.5).fillRoundedRect(p.x + 4, p.y + 6, p.w, p.h, 6); // cast shadow
      pg.fillStyle(COLORS.platform, 1).fillRoundedRect(p.x, p.y, p.w, p.h, 6); // body
      pg.fillStyle(0x6b72a4, 1).fillRect(p.x + 4, p.y, p.w - 8, 6); // lit tread (walk surface)
      pg.lineStyle(2, COLORS.platformEdge, 1).strokeRoundedRect(p.x, p.y, p.w, p.h, 6);
    }

    const lanes = this.level.paths;
    if (lanes.length > 1) {
      // Branching stage: the serpentine generator can't describe a fork, so draw
      // the steps straight from each authored lane's descending segments, then
      // each lane ribbon. Shared segments overlap harmlessly.
      this.drawStairsFromPaths(lanes);
      for (const lane of lanes) this.drawLane(lane);
    } else {
      this.drawStairwells();
      this.drawLane(path);
    }
    this.drawProps();

    // Universal slots: a building PLOT (a foundation pad on the ledge, not a
    // floating circle) marks where you can build. Hidden once a unit occupies it.
    const buttress = this.add.graphics().setDepth(1); // stone perches under off-platform slots
    for (const s of slots) {
      const padY = s.y + BUILD_BASE_OFFSET; // the platform top, where a building's base lands
      // A slot that doesn't stand on a platform (a stairwell overlook) gets a
      // built-out buttress so its building reads as resting on stone, not floating.
      const grounded = platforms.some(
        (p) => s.x >= p.x && s.x <= p.x + p.w && Math.abs(padY - p.y) < 16,
      );
      if (!grounded) {
        const lw = 72;
        const lh = 50;
        const lx = s.x - lw / 2;
        buttress.fillStyle(0x0e1020, 0.5).fillRoundedRect(lx + 4, padY + 6, lw, lh, 6); // cast shadow
        buttress.fillStyle(COLORS.platform, 1).fillRoundedRect(lx, padY, lw, lh, 6); // body
        buttress.fillStyle(0x6b72a4, 1).fillRect(lx + 4, padY, lw - 8, 6); // lit tread
        buttress.lineStyle(2, COLORS.platformEdge, 1).strokeRoundedRect(lx, padY, lw, lh, 6);
      }
      const ring = this.add.graphics().setDepth(5);
      ring.fillStyle(COLORS.slot, 0.22).fillRoundedRect(s.x - 21, padY - 8, 42, 13, 4);
      ring.lineStyle(2, 0xffffff, 0.32).strokeRoundedRect(s.x - 21, padY - 8, 42, 13, 4);
      const label = this.add
        .text(s.x, padY - 22, '+', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          fontStyle: 'bold',
          color: '#e8eaf6',
        })
        .setOrigin(0.5)
        .setDepth(6);
      this.slotViews.set(s.id, { x: s.x, y: s.y, type: s.type, ring, label });
    }

    // One SPAWN per lane entrance. De-dupe lanes that share a mouth so single-path
    // and forking stages draw one banner, while a converging stage (two separate
    // forge-mouths feeding one gate) marks each mouth.
    const seenSpawn = new Set<string>();
    for (const lane of this.level.paths) {
      const start = lane[0];
      const key = `${Math.round(start.x)},${Math.round(start.y)}`;
      if (seenSpawn.has(key)) continue;
      seenSpawn.add(key);
      this.marker(start.x, start.y, 'SPAWN', 0x8a90c0, 'world_spawn', 104);
    }
    // One GATE per lane exit. De-dupe lanes that share an exit so single-path and
    // reconverging stages draw exactly one gate there.
    const seenGate = new Set<string>();
    for (const lane of this.level.paths) {
      const end = lane[lane.length - 1];
      const key = `${Math.round(end.x)},${Math.round(end.y)}`;
      if (seenGate.has(key)) continue;
      seenGate.add(key);
      this.marker(end.x, end.y, 'GATE', COLORS.gate, 'world_gate', 120);
    }
  }

  /** Draw discrete step treads along every DESCENDING segment of each lane — the
   *  branching-stage analogue of `drawStairwells` (which assumes one serpentine
   *  chain). Flat platform-top runs (near-horizontal) get no steps. */
  private drawStairsFromPaths(lanes: { x: number; y: number }[][]): void {
    const g = this.add.graphics().setDepth(1);
    const drawn = new Set<string>();
    for (const lane of lanes) {
      for (let i = 0; i < lane.length - 1; i++) {
        const a = lane[i];
        const b = lane[i + 1];
        if (b.y - a.y < 30) continue; // only real descents become stairs
        const key = `${Math.round(a.x)},${Math.round(a.y)}->${Math.round(b.x)},${Math.round(b.y)}`;
        if (drawn.has(key)) continue; // shared segment already stepped
        drawn.add(key);
        const n = Math.max(2, Math.round((b.y - a.y) / 34));
        const dx = (b.x - a.x) / n;
        const dy = (b.y - a.y) / n;
        const treadW = Math.max(34, Math.abs(dx) + 22);
        for (let s = 0; s < n; s++) {
          const cx = a.x + dx * (s + 0.5);
          const cy = a.y + dy * (s + 0.5);
          const x = cx - treadW / 2;
          g.fillStyle(0x0e1020, 0.45).fillRect(x + 3, cy + 4, treadW, 16); // shadow
          g.fillStyle(COLORS.platform, 1).fillRect(x, cy, treadW, 18); // step body
          g.fillStyle(0x6b72a4, 1).fillRect(x, cy, treadW, 4); // lit tread
        }
      }
    }
  }

  private strokePolyline(g: Phaser.GameObjects.Graphics, pts: { x: number; y: number }[]): void {
    g.beginPath();
    g.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
    g.strokePath();
  }

  /** Render the stairwells joining the platforms as discrete diagonal steps the
   *  enemies descend. Endpoints come from `stairwellSegments` so the steps sit
   *  exactly under the generated walk path (no drift). */
  private drawStairwells(): void {
    const g = this.add.graphics().setDepth(1);
    for (const s of stairwellSegments(this.level)) {
      const n = Math.max(1, s.steps);
      const dx = (s.bx - s.ax) / n;
      const dy = (s.by - s.ay) / n;
      const treadW = Math.max(34, Math.abs(dx) + 22); // overlap so it reads continuous
      for (let i = 0; i < n; i++) {
        const cx = s.ax + dx * (i + 0.5);
        const cy = s.ay + dy * (i + 0.5);
        const x = cx - treadW / 2;
        g.fillStyle(0x0e1020, 0.45).fillRect(x + 3, cy + 4, treadW, 16); // shadow
        g.fillStyle(COLORS.platform, 1).fillRect(x, cy, treadW, 18); // step body
        g.fillStyle(0x6b72a4, 1).fillRect(x, cy, treadW, 4); // lit tread
      }
    }
  }

  /** A faint guide along the generated walk path — the platforms + stairwells are
   *  the real surface; this just telegraphs the route enemies take. */
  private drawLane(path: { x: number; y: number }[]): void {
    const g = this.add.graphics().setDepth(2);
    g.lineStyle(4, COLORS.path, 0.16);
    this.strokePolyline(g, path);
  }

  /** Scatter Olympus dressing on the marble ledges — columns, braziers, statues,
   *  rubble — to frame the lane. Background depth, beneath every entity/slot. */
  private drawProps(): void {
    // These props are hand-placed against stage 1's serpentine ledges; other
    // stages dress themselves via their own layout (skip rather than float them).
    if (this.level.id !== 'stage_01_olympus') return;
    // Pinned to the new platform tops (origin 0.5,1 → base rests on the ledge),
    // tucked toward the platform corners so they frame but don't block the lane.
    const props: { key: string; x: number; y: number; h: number }[] = [
      { key: 'world_statue', x: 230, y: 240, h: 104 }, // P0
      { key: 'world_column', x: 640, y: 240, h: 72 },
      { key: 'world_column', x: 1110, y: 470, h: 72 }, // P1
      { key: 'world_brazier', x: 752, y: 470, h: 78 },
      { key: 'world_column', x: 188, y: 700, h: 72 }, // P2
      { key: 'world_column_broken', x: 760, y: 700, h: 42 },
      { key: 'world_rubble', x: 786, y: 930, h: 44 }, // P3
      { key: 'world_column', x: 1160, y: 930, h: 72 },
      { key: 'world_brazier', x: 152, y: 1160, h: 78 }, // P4
      { key: 'world_statue', x: 648, y: 1160, h: 104 },
      { key: 'world_column', x: 1160, y: 1390, h: 72 }, // P5
      { key: 'world_column_broken', x: 200, y: 1620, h: 42 }, // P6
      { key: 'world_column', x: 762, y: 1620, h: 72 },
      { key: 'world_brazier', x: 736, y: 1850, h: 78 }, // P7
      { key: 'world_column', x: 1160, y: 1850, h: 72 },
    ];
    // Columns/broken columns are FRAMING set behind the steps (depth 0.5, under
    // the platforms+stairs at depth 1) so the staircase reads in front of them;
    // statues/braziers/rubble are foreground dressing on the ledges (depth 4).
    const BEHIND_STEPS = new Set(['world_column', 'world_column_broken']);
    // Per-asset seat nudge for art with empty space below its base (the brazier's
    // tripod feet sit ~11px above its viewBox bottom) so props rest on the stone.
    const SEAT: Record<string, number> = { world_brazier: 12 };
    for (const p of props) {
      if (!this.textures.exists(p.key)) continue;
      const img = this.add
        .image(p.x, p.y + (SEAT[p.key] ?? 0), p.key)
        .setDepth(BEHIND_STEPS.has(p.key) ? 0.5 : 4)
        .setOrigin(0.5, 1);
      img.setScale(p.h / img.height); // uniform scale preserves the prop's aspect
    }
  }

  private marker(
    x: number,
    y: number,
    label: string,
    color: number,
    textureKey?: string,
    size = 44,
  ): void {
    // Depth 8: the spawn/gate portals read in front of buildings (7) but still
    // behind the marching enemies (10) — the lane stays on top.
    if (textureKey && this.textures.exists(textureKey)) {
      this.add.image(x, y, textureKey).setDisplaySize(size, size).setDepth(8);
    } else {
      this.add.circle(x, y, 22, color, 0.25).setStrokeStyle(2, color, 0.8).setDepth(8);
    }
    this.add
      .text(x, y - size / 2 - 12, label, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#e8eaf6',
      })
      .setOrigin(0.5)
      .setDepth(8);
  }

  // ---- tap handling (camera/input rig lives in CameraController) -----------

  /** Tap → select the nearest build slot (CameraController hands us world coords
   *  once it's distinguished a tap from a pan/pinch). */
  private handleTap(worldX: number, worldY: number): void {
    let hit: string | null = null;
    let bestD2 = 32 * 32; // generous tap radius in world units
    for (const [id, v] of this.slotViews) {
      const d2 = (worldX - v.x) ** 2 + (worldY - v.y) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        hit = id;
      }
    }
    this.selectSlot(hit);
  }

  selectSlot(slotId: string | null): void {
    this.selectedSlotId = slotId;
    // Changing selection abandons any in-flight draft / sell confirmation.
    this.draftUnitId = null;
    this.pendingSell = false;
    this.uiVersion++;
  }

  // ---- HUD-facing action proxies (operate on the selected slot) -----------

  actDeploy(unitId: string): void {
    // Sound the cue only on a real change — a failed (e.g. unaffordable) action
    // shouldn't lie with a confirming thunk.
    if (this.selectedSlotId && this.world.deploy(this.selectedSlotId, unitId).ok) sfx.play('deploy');
    this.draftUnitId = null;
    this.uiVersion++;
  }
  /** Stage a unit for placement (draft) — visualized, not yet built. */
  actBeginDeploy(unitId: string): void {
    this.draftUnitId = unitId;
    sfx.play('click');
    this.uiVersion++;
  }
  /** Drop the staged unit and return to the picker. */
  actCancelDeploy(): void {
    this.draftUnitId = null;
    sfx.play('click');
    this.uiVersion++;
  }
  /** Commit the staged unit (charges gold, builds it). */
  actConfirmDeploy(): void {
    const unitId = this.draftUnitId;
    if (unitId) this.actDeploy(unitId);
    else this.uiVersion++;
  }
  actUpgrade(branchId?: string): void {
    if (this.selectedSlotId && this.world.upgrade(this.selectedSlotId, branchId).ok) sfx.play('upgrade');
    this.uiVersion++;
  }
  /** Arm the sell confirmation (the Sell button's first tap). */
  actBeginSell(): void {
    this.pendingSell = true;
    sfx.play('click');
    this.uiVersion++;
  }
  actCancelSell(): void {
    this.pendingSell = false;
    sfx.play('click');
    this.uiVersion++;
  }
  actSell(): void {
    if (this.selectedSlotId && this.world.sell(this.selectedSlotId).ok) sfx.play('sell');
    this.selectedSlotId = null;
    this.pendingSell = false;
    this.uiVersion++;
  }
  actSetTargeting(mode: TargetMode): void {
    if (this.selectedSlotId && this.world.setTargeting(this.selectedSlotId, mode).ok) sfx.play('click');
    this.uiVersion++;
  }
  actCallWave(): void {
    this.world.startOrCallNextWave();
    this.uiVersion++;
  }

  // ---- HUD control surface (tempo) ----------------------------------------

  setSpeed(n: number): void {
    this.speed = n;
  }
  togglePause(): void {
    this.paused = !this.paused;
  }
  restartBattle(): void {
    this.scene.restart({ levelId: this.levelId, difficultyTier: this.difficultyTier });
  }

  // ---- fixed-step sim + render --------------------------------------------

  update(_time: number, delta: number): void {
    const playing = this.world.phase !== 'won' && this.world.phase !== 'lost';
    if (!this.paused && playing) {
      this.accumulatorMs += delta * this.speed;
      const stepMs = 1000 / SIM.hz;
      let steps = 0;
      while (this.accumulatorMs >= stepMs && steps < SIM.maxStepsPerFrame) {
        this.world.step(SIM.dt);
        this.accumulatorMs -= stepMs;
        steps++;
      }
      if (steps >= SIM.maxStepsPerFrame) this.accumulatorMs = 0;
    }
    this.processEvents(this.world.drainEvents());
    this.checkPhaseAudio();
    this.renderEnemies();
    this.renderUnits(delta / 1000);
    this.renderFx(delta / 1000);
    this.renderOverlay();
  }

  /** Sound the battle-flow cues on phase changes: a war-horn when a wave begins
   *  (prep→spawning or intermission→spawning) and the win/lose stingers. */
  private checkPhaseAudio(): void {
    const phase = this.world.phase;
    if (phase === this.lastPhase) return;
    if (this.lastPhase !== '') {
      if (phase === 'spawning') sfx.play('wave_start');
      else if (phase === 'won') sfx.play('victory');
      else if (phase === 'lost') sfx.play('defeat');
    }
    this.lastPhase = phase;
  }

  private renderEnemies(): void {
    const seen = new Set<Enemy>();
    for (const e of this.world.enemies) {
      seen.add(e);
      let v = this.enemyViews.get(e);
      const created = !v;
      if (!v) {
        // Prefer the authored token; tint stays neutral so the art shows true.
        // Fall back to the procedural circle (tinted per type) if absent.
        const key = `enemy_${e.defId}`;
        const hasToken = this.textures.exists(key);
        const baseTint = hasToken ? 0xffffff : ENEMY_TINT[e.defId] ?? 0xe06c6c;
        const body = this.add.image(0, 0, hasToken ? key : 'enemy');
        if (!hasToken) body.setTint(baseTint);
        const d = ENEMY_SIZE[e.defId] ?? ENEMY_SIZE_DEFAULT;
        body.setDisplaySize(d, d);
        const hp = this.add.rectangle(0, -d / 2 - 6, 24, 4, 0x6cf08a).setOrigin(0.5);
        const children: Phaser.GameObjects.GameObject[] = [];
        // Flyers ride above the lane with a ground shadow so "it ignores
        // blockers" reads at a glance.
        let shadow: Phaser.GameObjects.Ellipse | undefined;
        if (e.flying) {
          shadow = this.add.ellipse(0, d * 0.42, d * 0.55, d * 0.18, 0x10131f, 0.35);
          body.y = -d * 0.55;
          hp.y = body.y - d / 2 - 6;
          children.push(shadow);
        }
        // "Exposed" chevron above the head — a warm-red ▼ that's tint-independent
        // so it stacks with the frost-blue slow tint.
        const expose = this.add
          .text(0, hp.y - 11, '▼', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '15px',
            fontStyle: 'bold',
            color: '#ff6a4a',
          })
          .setOrigin(0.5)
          .setVisible(false);
        // Frost rime overlay — shown only while chilled, atop the blue tint.
        let frost: Phaser.GameObjects.Image | undefined;
        if (this.textures.exists('fx_frost')) {
          frost = this.add.image(body.x, body.y, 'fx_frost').setDisplaySize(d, d).setVisible(false);
        }
        children.push(body, hp, expose);
        if (frost) children.push(frost);
        const container = this.add.container(0, 0, children).setDepth(e.flying ? 13 : 10);
        v = { container, body, hp, shadow, expose, frost, baseTint, flying: e.flying };
        this.enemyViews.set(e, v);
      }
      const p = e.position(this.world.pathFor(e));
      v.container.setPosition(p.x, p.y);
      // The Unmade tear into the world — flash the rift as each one appears.
      if (created) this.spawnRiftFlash(p.x, p.y);
      const frac = Phaser.Math.Clamp(e.hp / e.maxHp, 0, 1);
      v.hp.width = 24 * frac;
      v.hp.fillColor = frac > 0.5 ? 0x6cf08a : frac > 0.25 ? 0xe0c04a : 0xe0556c;
      v.hp.setVisible(frac < 1);
      // Chilled enemies read as frost-blue, with a shimmering rime overlay.
      const chilled = e.slowRemaining > 0;
      v.body.setTint(chilled ? 0x7fc6ff : v.baseTint);
      if (v.frost) {
        v.frost.setVisible(chilled);
        if (chilled) v.frost.setAlpha(0.5 + 0.28 * Math.sin(this.time.now / 160));
      }
      // Exposed (held by a blocker with the expose perk) pulses the ▼ cue.
      const exposed = this.enemyExposed(e);
      v.expose.setVisible(exposed);
      if (exposed) v.expose.setAlpha(0.55 + 0.45 * Math.sin(this.time.now / 110));
    }
    for (const [e, v] of this.enemyViews) {
      if (!seen.has(e)) {
        v.container.destroy();
        this.enemyViews.delete(e);
      }
    }
  }

  /** True if the enemy is held by a blocker whose current tier has `expose`
   *  (the +damage debuff World applies in unitHit) — drives the ▼ cue. */
  private enemyExposed(e: Enemy): boolean {
    const slotId = e.engagedUnitSlot;
    if (!slotId) return false;
    const u = this.world.units.get(slotId);
    if (!u) return false;
    return this.world.data.units.get(u.defId)?.tiers[u.tier]?.perks?.includes('expose') ?? false;
  }

  /** The token texture for a unit: a branch-specific token once specialized
   *  (the visible upgrade payoff), falling back to the base unit token. */
  private unitTokenKey(u: Unit): string {
    if (u.branchId) {
      const branched = `unit_${u.defId}_${u.branchId}`;
      if (this.textures.exists(branched)) return branched;
    }
    return `unit_${u.defId}`;
  }

  /** A unit's BUILDING sprite (authored side-view art, or a stone-keep fallback),
   *  base-anchored on the platform top so it stands on the ledge. Height comes
   *  from the building spec; width preserves the art's aspect. */
  private makeBuildingToken(u: Unit, spec: BuildingSpec): Phaser.GameObjects.GameObject {
    const key = this.unitTokenKey(u);
    if (this.textures.exists(key)) {
      const img = this.add.image(0, BUILD_BASE_OFFSET, key).setOrigin(0.5, 1);
      img.setScale(spec.h / img.height); // height to spec, aspect preserved
      return img;
    }
    // Fallback: a plain stone keep with the family glyph.
    const w = spec.h * 0.7;
    const top = BUILD_BASE_OFFSET - spec.h;
    const g = this.add.graphics();
    g.fillStyle(0x2b2f4a, 1).fillRect(-w / 2, top, w, spec.h);
    g.lineStyle(2, FAMILY_COLOR[u.family] ?? 0x565d8c, 1).strokeRect(-w / 2, top, w, spec.h);
    const glyph = this.add
      .text(0, top + spec.h / 2, unitGlyph(u.family, u.isHero), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#e8eaf6',
      })
      .setOrigin(0.5);
    return this.add.container(0, 0, [g, glyph]);
  }

  /** A small distinct soldier body — a family-coloured disc with a sword (or ♛
   *  for the hero's champion). Deliberately NOT the unit art: the building keeps
   *  that; these are the little fighters it sends out. */
  private makeSoldierToken(u: Unit): Phaser.GameObjects.Container {
    const color = FAMILY_COLOR[u.family] ?? 0xc05a5a;
    const r = u.isHero ? 12 : 10;
    const base = this.add.circle(0, 0, r, color, 1);
    base.setStrokeStyle(2.5, u.isHero ? 0xffe08a : 0x10131f, u.isHero ? 1 : 0.9);
    const glyph = this.add
      .text(0, 0, u.isHero ? '♛' : '⚔', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: u.isHero ? '13px' : '12px',
        fontStyle: 'bold',
        color: '#10131f',
      })
      .setOrigin(0.5);
    return this.add.container(0, 0, [base, glyph]);
  }

  /** A single crew figure that stands on a ranged building's deck — a little
   *  archer (bow drawn) or a hovering arcane mote. One is shown per multiShot, so
   *  upgrading a watchtower visibly mans it with more archers. */
  private makeCrewFigure(kind: 'archer' | 'mote', family: string): Phaser.GameObjects.Container {
    if (kind === 'mote') {
      const halo = this.add.circle(0, -6, 5, 0xaab4ff, 0.28);
      const core = this.add.circle(0, -6, 2.6, 0xcdd4ff, 1).setStrokeStyle(1, 0x7e8af0, 0.9);
      return this.add.container(0, 0, [halo, core]);
    }
    const c = FAMILY_COLOR[family] ?? 0x5ac0a0;
    const body = this.add.ellipse(0, -5, 6, 10, c).setStrokeStyle(1, 0x10131f, 0.9);
    const head = this.add.circle(0, -11, 2.6, 0xe8eaf6).setStrokeStyle(1, 0x10131f, 0.8);
    const bow = this.add.graphics();
    bow.lineStyle(1.4, 0x9a6b3a, 1);
    bow.beginPath();
    bow.arc(4, -6, 6, -1.1, 1.1);
    bow.strokePath();
    bow.lineStyle(1, 0xcfd4ea, 0.9);
    bow.beginPath();
    bow.moveTo(4 + 6 * Math.cos(-1.1), -6 + 6 * Math.sin(-1.1));
    bow.lineTo(4 + 6 * Math.cos(1.1), -6 + 6 * Math.sin(1.1));
    bow.strokePath();
    return this.add.container(0, 0, [body, head, bow]);
  }

  private renderUnits(dt: number): void {
    for (const [slotId, u] of this.world.units) {
      const key = this.unitTokenKey(u);
      const squadN = u.isMelee ? u.soldiers.length : 0;
      let v = this.unitViews.get(slotId);
      // A branch upgrade swaps the art, and an "extra body" upgrade changes the
      // squad size — rebuild the view when either the token or body count moves.
      if (v && (v.key !== key || v.soldiers.length !== squadN)) {
        this.destroyUnitView(v);
        this.unitViews.delete(slotId);
        v = undefined;
      }
      if (!v) v = this.buildUnitView(slotId, u, key, squadN);

      const def = UNITS.get(u.defId);
      const level = def ? displayLevel(def, u.tier, u.branchId) : u.tier + 1;
      v.pips.setText('★'.repeat(level));

      if (u.isMelee) {
        this.renderSquad(v, u, dt);
      } else {
        this.renderCrew(v, u);
      }
    }
    // Remove views for sold/destroyed units.
    for (const [slotId, v] of this.unitViews) {
      if (!this.world.units.has(slotId)) {
        this.destroyUnitView(v);
        this.unitViews.delete(slotId);
      }
    }
  }

  private destroyUnitView(v: UnitView): void {
    v.container.destroy(); // building + pips + shot icons
    for (const sv of v.soldiers) sv.container.destroy();
  }

  /** Build a unit's view: the stationary BUILDING token on the slot, plus (for
   *  melee) a set of small soldier bodies that live in world space and march out
   *  to the path to engage. */
  private buildUnitView(slotId: string, u: Unit, key: string, squadN: number): UnitView {
    const spec = BUILDINGS[u.defId] ?? BUILDING_FALLBACK;
    const pips = this.add
      .text(0, BUILD_BASE_OFFSET - spec.h - 9, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#e0b04a',
      })
      .setOrigin(0.5);
    const building = this.makeBuildingToken(u, spec);
    // Depth 7: behind enemies (10) + soldiers (11), so foes pass IN FRONT of the
    // structure on the ledge; the selection/range overlay (8) still draws over it.
    const container = this.add.container(u.x, u.y, [building, pips]).setDepth(7);

    const soldiers: SoldierView[] = [];
    if (u.isMelee) {
      for (let i = 0; i < squadN; i++) {
        const token = this.makeSoldierToken(u);
        const hp = this.add.rectangle(0, SOLDIER_TOKEN / 2 + 2, 18, 3, 0x6cf08a).setOrigin(0.5);
        const respawn = this.add
          .text(0, -SOLDIER_TOKEN / 2 - 6, '', {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '10px',
            fontStyle: 'bold',
            color: '#e0556c',
          })
          .setOrigin(0.5)
          .setVisible(false);
        // Soldiers ride just beneath the building depth so the structure reads
        // as "behind" the fighters it sent forward.
        const sc = this.add.container(u.x, u.y, [token, hp, respawn]).setDepth(11);
        soldiers.push({ container: sc, token, hp, respawn, x: u.x, y: u.y, placed: false });
      }
    }
    const size = this.buildingSize(u, spec);
    const v: UnitView = {
      container,
      pips,
      key,
      soldiers,
      shotCount: 0,
      bw: size.w / 2,
      btop: BUILD_BASE_OFFSET - size.h,
      bbot: BUILD_BASE_OFFSET,
    };
    this.unitViews.set(slotId, v);
    return v;
  }

  /** A building's on-screen footprint (display width/height), preserving the
   *  authored art's aspect. Shared by the token, its selection outline, and the
   *  placement ghost so they all agree. */
  private buildingSize(u: Unit, spec: BuildingSpec): { w: number; h: number } {
    const key = this.unitTokenKey(u);
    if (this.textures.exists(key)) {
      const src = this.textures.get(key).getSourceImage() as { width: number; height: number };
      return { w: (spec.h * src.width) / src.height, h: spec.h };
    }
    return { w: spec.h * 0.7, h: spec.h };
  }

  /** Where soldier `i` of `n` should stand: at the foe it holds (engagedDistance,
   *  pulled back a touch so it fights beside — not on — the enemy), else at the
   *  squad's hold line. Fanned perpendicular to the path so bodies don't stack. */
  private soldierTarget(u: Unit, i: number, n: number, engagedDistance: number | null): { x: number; y: number } {
    // Stand on the lane this squad holds (the primary/nearest lane on a fork).
    const path = this.world.paths[u.blockLane] ?? this.world.path;
    const d = engagedDistance != null ? Math.max(0, engagedDistance - SOLDIER_STANDOFF) : u.blockDistance;
    const p = path.getPointAt(d);
    const a = path.getPointAt(Math.max(0, d - 4));
    const b = path.getPointAt(d + 4);
    let tx = b.x - a.x;
    let ty = b.y - a.y;
    const tl = Math.hypot(tx, ty) || 1;
    tx /= tl;
    ty /= tl;
    const off = (i - (n - 1) / 2) * SQUAD_SPACING;
    return { x: p.x - ty * off, y: p.y + tx * off };
  }

  /** Per-soldier render: march living bodies toward their foe/hold line, show
   *  their HP bar (hidden at full), and fade + count down the fallen in place. */
  private renderSquad(v: UnitView, u: Unit, dt: number): void {
    const n = v.soldiers.length;
    const step = SOLDIER_SPEED * Math.max(1, this.speed) * dt;
    for (let i = 0; i < n; i++) {
      const sv = v.soldiers[i];
      const s = u.soldiers[i];
      if (!s) continue;
      if (s.dead) {
        sv.container.setAlpha(0.3).setPosition(sv.x, sv.y); // fall where it stood
        sv.hp.setVisible(false);
        sv.respawn.setVisible(true).setText(`⟳ ${Math.ceil(s.respawnRemaining)}s`);
        continue;
      }
      sv.container.setAlpha(1);
      sv.respawn.setVisible(false);
      const t = this.soldierTarget(u, i, n, s.engagedDistance);
      if (!sv.placed) {
        sv.x = u.x; // first appearance: march out from the building
        sv.y = u.y;
        sv.placed = true;
      }
      const dx = t.x - sv.x;
      const dy = t.y - sv.y;
      const dist = Math.hypot(dx, dy);
      if (dist <= step || dist < 0.5) {
        sv.x = t.x;
        sv.y = t.y;
      } else {
        sv.x += (dx / dist) * step;
        sv.y += (dy / dist) * step;
      }
      sv.container.setPosition(sv.x, sv.y);
      const frac = s.maxHp > 0 ? Phaser.Math.Clamp(s.hp / s.maxHp, 0, 1) : 1;
      sv.hp.setVisible(frac < 1);
      sv.hp.width = 18 * frac;
      sv.hp.fillColor = frac > 0.5 ? 0x6cf08a : frac > 0.25 ? 0xe0c04a : 0xe0556c;
    }
  }

  /** Ranged buildings are manned: one crew figure per multiShot stands on the
   *  firing deck (the visible "extra archer" payoff). Rebuilt only when the count
   *  changes. Melee buildings have no crew — they send soldiers out instead. */
  private renderCrew(v: UnitView, u: Unit): void {
    const spec = BUILDINGS[u.defId];
    if (!spec?.crew) return;
    const count = Phaser.Math.Clamp(Math.round(u.stats.multiShot) || 1, 1, 4);
    if (count === v.shotCount) return;
    v.shotCount = count;
    v.shots?.destroy();
    const step = count > 1 ? spec.crew.spread / (count - 1) : 0;
    const figs: Phaser.GameObjects.GameObject[] = [];
    for (let i = 0; i < count; i++) {
      const f = this.makeCrewFigure(spec.crew.kind, u.family);
      f.x = (i - (count - 1) / 2) * step;
      figs.push(f);
    }
    v.shots = this.add.container(0, spec.crew.y, figs);
    v.container.add(v.shots);
  }

  private processEvents(events: SimEvent[]): void {
    for (const ev of events) {
      switch (ev.kind) {
        case 'shot': {
          // Flyers render lifted above the lane, so aim the projectile at the
          // sprite (not the ground point) — otherwise hits look like misses.
          const lift = ev.toFlying ? (ENEMY_SIZE['harpy'] ?? 42) * 0.55 : 0;
          this.spawnProjectile(ev.fromX, ev.fromY, ev.toX, ev.toY - lift, ev.family);
          sfx.play(SHOT_SFX[ev.family] ?? 'shot_ranged');
          break;
        }
        case 'damage':
          this.spawnFloat(ev.x, ev.y, `${Math.round(ev.amount)}`, ev.crit ? '#ffd34d' : '#e8eaf6', ev.crit);
          sfx.play(ev.crit ? 'crit' : 'hit');
          break;
        case 'death':
          this.spawnFloat(ev.x, ev.y - 8, `+${ev.bounty}`, '#e0b04a', false);
          this.spawnShatter(ev.x, ev.y, ENEMY_SIZE[ev.defId] ?? ENEMY_SIZE_DEFAULT);
          sfx.play('death');
          break;
        case 'leak':
          this.spawnFloat(ev.x, ev.y, `-${ev.cost}`, '#e0556c', true);
          this.cameras.main.shake(120, 0.004);
          sfx.play('leak');
          break;
        case 'unitDeath':
          this.spawnFloat(ev.x, ev.y - 8, 'Fallen', '#e0556c', false);
          sfx.play('unit_death');
          break;
        case 'unitRespawn':
          this.spawnFloat(ev.x, ev.y - 8, 'Rallied', '#6cf08a', false);
          sfx.play('unit_respawn');
          break;
      }
    }
  }

  private spawnFloat(x: number, y: number, text: string, color: string, big: boolean): void {
    const t =
      this.floatPool.pop() ??
      this.add.text(0, 0, '', { fontFamily: 'system-ui, sans-serif', fontStyle: 'bold' }).setDepth(20);
    t.setText(text).setColor(color).setFontSize(big ? 22 : 16).setPosition(x, y).setAlpha(1).setOrigin(0.5).setVisible(true);
    this.floats.push({ text: t, life: 0.65, vy: -42 });
  }

  /** Fling a projectile sprite from shooter to target; burst on arrival.
   *  Falls back to a brief line tracer if the family's FX texture is missing. */
  private spawnProjectile(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    family: string,
  ): void {
    const key = PROJECTILE_FX[family];
    if (!key || !this.textures.exists(key)) {
      this.tracers.push({
        fromX,
        fromY,
        toX,
        toY,
        life: 0.12,
        color: FAMILY_COLOR[family] ?? 0xffffff,
      });
      return;
    }
    const dx = toX - fromX;
    const dy = toY - fromY;
    const dist = Math.hypot(dx, dy) || 1;
    const img = this.add.image(fromX, fromY, key).setDepth(14);
    const sz = PROJECTILE_SIZE[key] ?? 22;
    img.setDisplaySize(sz, sz);
    if (key === 'fx_arrow') img.setRotation(Math.atan2(dy, dx));
    // Flight time scales with distance but is bounded; divide by game speed so
    // shots still land roughly in step at 2×/3×.
    const dur = (Phaser.Math.Clamp(dist / 900, 0.05, 0.18) * 1000) / Math.max(1, this.speed);
    this.tweens.add({
      targets: img,
      x: toX,
      y: toY,
      duration: dur,
      ease: 'Linear',
      onComplete: () => {
        img.destroy();
        this.spawnImpact(toX, toY);
      },
    });
  }

  /** Death burst: an Unmade enemy shatters into void-lit shards. Sized to the
   *  enemy so the colossus erupts while a swarmling pops. */
  private spawnShatter(x: number, y: number, size: number): void {
    if (!this.textures.exists('fx_shatter')) return;
    const from = size * 0.7;
    const to = size * 1.8;
    const s = this.add.image(x, y, 'fx_shatter').setDepth(15).setAlpha(0.95).setDisplaySize(from, from);
    this.tweens.add({
      targets: s,
      displayWidth: to,
      displayHeight: to,
      alpha: 0,
      angle: 35,
      duration: 320,
      ease: 'Quad.easeOut',
      onComplete: () => s.destroy(),
    });
  }

  /** Spawn flash: a void rift flares as an enemy tears into the world. */
  private spawnRiftFlash(x: number, y: number): void {
    if (!this.textures.exists('fx_rift_flash')) return;
    const s = this.add.image(x, y, 'fx_rift_flash').setDepth(9).setDisplaySize(18, 18);
    this.tweens.add({
      targets: s,
      alpha: { from: 0.9, to: 0 },
      displayWidth: 70,
      displayHeight: 70,
      duration: 380,
      ease: 'Quad.easeOut',
      onComplete: () => s.destroy(),
    });
  }

  /** A quick expanding flash where a projectile lands. */
  private spawnImpact(x: number, y: number): void {
    if (!this.textures.exists('fx_impact')) return;
    const s = this.add.image(x, y, 'fx_impact').setDepth(15).setDisplaySize(12, 12).setAlpha(0.95);
    this.tweens.add({
      targets: s,
      displayWidth: 36,
      displayHeight: 36,
      alpha: 0,
      duration: 200,
      ease: 'Quad.easeOut',
      onComplete: () => s.destroy(),
    });
  }

  private renderFx(dt: number): void {
    this.fxLayer.clear();
    for (let i = this.tracers.length - 1; i >= 0; i--) {
      const tr = this.tracers[i];
      tr.life -= dt;
      if (tr.life <= 0) {
        this.tracers.splice(i, 1);
        continue;
      }
      this.fxLayer.lineStyle(2, tr.color, Phaser.Math.Clamp(tr.life / 0.12, 0, 1));
      this.fxLayer.beginPath();
      this.fxLayer.moveTo(tr.fromX, tr.fromY);
      this.fxLayer.lineTo(tr.toX, tr.toY);
      this.fxLayer.strokePath();
    }
    for (let i = this.floats.length - 1; i >= 0; i--) {
      const f = this.floats[i];
      f.life -= dt;
      if (f.life <= 0) {
        f.text.setVisible(false);
        this.floatPool.push(f.text);
        this.floats.splice(i, 1);
        continue;
      }
      f.text.y += f.vy * dt;
      f.text.setAlpha(Phaser.Math.Clamp(f.life / 0.65, 0, 1));
    }
  }

  private renderOverlay(): void {
    this.overlay.clear();
    // Pulse empty unlocked slots so the player knows where to build.
    const pulse = 0.25 + 0.15 * Math.sin(this.time.now / 250);
    for (const [id, v] of this.slotViews) {
      const occupied = this.world.units.has(id);
      const selected = id === this.selectedSlotId;
      // The build plot + '+' only read as "buildable here" while empty.
      v.ring.setVisible(!occupied);
      v.label.setVisible(!occupied);
      if (!occupied) {
        // Pulse the foundation pad on the ledge (not a circle in the air).
        const padY = v.y + BUILD_BASE_OFFSET;
        this.overlay.lineStyle(2, 0xffffff, selected ? 0.9 : pulse);
        this.overlay.strokeRoundedRect(v.x - 23, padY - 10, 46, 17, 5);
      } else if (selected) {
        // Outline that HUGS the building footprint, base on the ledge.
        const b = this.unitViews.get(id);
        if (b) {
          this.overlay.lineStyle(2.5, 0xffffff, 0.9);
          this.overlay.strokeRoundedRect(v.x - b.bw - 3, v.y + b.btop - 3, b.bw * 2 + 6, b.bbot - b.btop + 6, 5);
        }
      }
    }
    // Range ring for a selected unit: teal = ranged fire range, warm red = the
    // melee squad's engagement zone (how far its soldiers march out to hold).
    if (this.selectedSlotId) {
      const u: Unit | undefined = this.world.units.get(this.selectedSlotId);
      if (u && u.range > 0) {
        const c = u.isMelee ? 0xc05a5a : 0x5ac0a0;
        this.overlay.fillStyle(c, 0.08);
        this.overlay.fillCircle(u.x, u.y, u.range);
        this.overlay.lineStyle(2, c, 0.6);
        this.overlay.strokeCircle(u.x, u.y, u.range);
      }
    }
    this.renderDraft();
  }

  /** Visualize a staged (un-built) unit: a translucent token plus its tier-1
   *  range ring, in the gold accent so it reads as "preview, not placed yet". */
  private renderDraft(): void {
    const slotId = this.selectedSlotId;
    const def = this.draftUnitId ? this.world.data.units.get(this.draftUnitId) : undefined;
    const slot = slotId ? this.slotViews.get(slotId) : undefined;
    if (!def || !slot) {
      this.draftToken?.setVisible(false);
      return;
    }
    const range = def.tiers[0].stats.range ?? 0;
    if (range > 0) {
      this.overlay.fillStyle(0xe0b04a, 0.1);
      this.overlay.fillCircle(slot.x, slot.y, range);
      this.overlay.lineStyle(2, 0xe0b04a, 0.7);
      this.overlay.strokeCircle(slot.x, slot.y, range);
    }
    // A gold ring on the target slot anchors the ghost.
    this.overlay.lineStyle(3, 0xe0b04a, 0.9);
    this.overlay.strokeCircle(slot.x, slot.y, 22);

    const key = `unit_${def.id}`;
    if (this.textures.exists(key)) {
      const spec = BUILDINGS[def.id] ?? BUILDING_FALLBACK;
      const src = this.textures.get(key).getSourceImage();
      const w = (spec.h * src.width) / src.height;
      if (!this.draftToken) this.draftToken = this.add.image(0, 0, key).setDepth(9);
      this.draftToken
        .setTexture(key)
        .setOrigin(0.5, 1)
        .setDisplaySize(w, spec.h) // ghost the building, base on the ledge
        .setPosition(slot.x, slot.y + BUILD_BASE_OFFSET)
        .setAlpha(0.6)
        .setVisible(true);
    } else {
      this.draftToken?.setVisible(false);
    }
  }
}
